import { paths } from '@/routes/paths'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { useBoolean } from '@/hooks/use-boolean'
import { Separator } from '@/components/ui/separator'
import { useRouter } from '@/routes/hooks/use-router'
import { useDeepEffect } from '@/hooks/use-deep-effect'
import { AuthProvider, Role, ShopStatus } from '@/types/user.type'
import { useAuthContext } from '@/contexts/auth-context/auth-provider'
import LoadingSection from '@/components/loading-indicator/loading-section'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import ChangePasswordDialog from '@/sections/profile/change-password-dialog.tsx'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import {
  User as UserIcon,
  Store,
  Mail,
  Phone,
  Calendar,
  MapPin,
  FileText,
} from 'lucide-react'

export default function ProfileView() {
  const router = useRouter()

  const { user } = useAuthContext()

  const isLoading = useBoolean(true)
  const openChangePasswordDialog = useBoolean(false)

  useDeepEffect(() => {
    if (user) {
      isLoading.onFalse()
    }
  }, [user])

  if (!user) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h2 className="text-2xl font-semibold mb-2">ไม่พบข้อมูลผู้ใช้</h2>
          <p className="text-muted-foreground">กรุณาเข้าสู่ระบบใหม่</p>
        </div>
      </div>
    )
  }

  const getRoleLabel = (role: Role) => {
    switch (role) {
      case Role.ADMIN:
        return 'ผู้ดูแลระบบ'
      case Role.SHOP:
        return 'ร้านค้า'
      case Role.USER:
        return 'ผู้ใช้'
      default:
        return role
    }
  }

  const getShopStatusLabel = (status: ShopStatus) => {
    switch (status) {
      case ShopStatus.OPEN:
        return 'เปิดทำการ'
      case ShopStatus.CLOSED:
        return 'ปิดทำการ'
      default:
        return status
    }
  }

  const isEmailProvider = user.authProvider === AuthProvider.EMAIL

  return (
    <LoadingSection loading={isLoading.value}>
      <div className="grid gap-6 md:grid-cols-2">
        {/* Profile Information */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <UserIcon className="h-5 w-5" />
              ข้อมูลส่วนตัว
            </CardTitle>
            <CardDescription>ข้อมูลพื้นฐานของบัญชีผู้ใช้</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-4">
              <Avatar className="h-16 w-16">
                <AvatarImage
                  src={user.imageUrl}
                  alt={user.displayName || 'User'}
                />
                <AvatarFallback className="text-lg">
                  {user.displayName?.charAt(0) || 'U'}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1">
                <h3 className="text-xl font-semibold">
                  {user.displayName || 'ไม่ระบุ'}
                </h3>
                <p className="text-muted-foreground">
                  {user.email || 'ไม่ระบุ'}
                </p>
                <Badge variant="secondary" className="mt-1">
                  {user.role ? getRoleLabel(user.role) : 'ไม่ระบุ'}
                </Badge>
              </div>
            </div>

            <Separator />

            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <Mail className="h-4 w-4 text-muted-foreground" />
                <div>
                  <p className="text-sm font-medium">อีเมล</p>
                  <p className="text-sm text-muted-foreground">
                    {user.email || 'ไม่ระบุ'}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <Phone className="h-4 w-4 text-muted-foreground" />
                <div>
                  <p className="text-sm font-medium">เบอร์โทรศัพท์</p>
                  <p className="text-sm text-muted-foreground">
                    {user.phoneNumber || 'ไม่ระบุ'}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <Calendar className="h-4 w-4 text-muted-foreground" />
                <div>
                  <p className="text-sm font-medium">วันที่สมัคร</p>
                  <p className="text-sm text-muted-foreground">
                    {user.createdAt
                      ? new Date(user.createdAt).toLocaleDateString('th-TH')
                      : 'ไม่ระบุ'}
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Shop Information (if user is SHOP) */}
        {user.role === Role.SHOP && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Store className="h-5 w-5" />
                ข้อมูลร้านค้า
              </CardTitle>
              <CardDescription>ข้อมูลร้านค้าของคุณ</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <Store className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <p className="text-sm font-medium">สถานะร้านค้า</p>
                    <Badge
                      variant={
                        user.shopStatus === ShopStatus.OPEN
                          ? 'default'
                          : 'secondary'
                      }
                      className="mt-1"
                    >
                      {user.shopStatus
                        ? getShopStatusLabel(user.shopStatus)
                        : 'ไม่ระบุ'}
                    </Badge>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <MapPin className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <p className="text-sm font-medium">Line ID</p>
                    <p className="text-sm text-muted-foreground">
                      {user.lineId || 'ไม่ระบุ'}
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <FileText className="h-4 w-4 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-sm font-medium">คำอธิบายร้านค้า</p>
                    <div
                      className="text-sm text-muted-foreground mt-2 line-clamp-2 break-words text-ellipsis overflow-hidden prose prose-sm max-w-none"
                      // eslint-disable-next-line react-dom/no-dangerously-set-innerhtml
                      dangerouslySetInnerHTML={{
                        __html: user.shopDescription || '',
                      }}
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <p className="text-sm font-medium">รูปปกร้านค้า</p>
                {user.shopCoverUrl ? (
                  <img
                    src={user.shopCoverUrl}
                    alt="Shop Cover"
                    className="w-full h-32 object-cover rounded-lg"
                  />
                ) : (
                  <p className="text-sm text-muted-foreground">ไม่ระบุ</p>
                )}
              </div>

              <div className="space-y-2">
                <p className="text-sm font-medium">แบนเนอร์ร้านค้า</p>
                {user.shopBannerUrl ? (
                  <img
                    src={user.shopBannerUrl}
                    alt="Shop Banner"
                    className="w-full h-24 object-cover rounded-lg"
                  />
                ) : (
                  <p className="text-sm text-muted-foreground">ไม่ระบุ</p>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Account Information */}
        <Card className="md:col-span-2">
          <CardHeader>
            <CardTitle>ข้อมูลบัญชี</CardTitle>
            <CardDescription>ข้อมูลเพิ่มเติมของบัญชี</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <p className="text-sm font-medium">ชื่อ</p>
                <p className="text-sm text-muted-foreground">
                  {user.firstName && user.lastName
                    ? `${user.firstName} ${user.lastName}`
                    : user.firstName || user.lastName || 'ไม่ระบุ'}
                </p>
              </div>

              {/* <div className="space-y-2">
                <p className="text-sm font-medium">ตำแหน่ง</p>
                <p className="text-sm text-muted-foreground">
                  {user.title || 'ไม่ระบุ'}
                </p>
              </div> */}

              <div className="space-y-2">
                <p className="text-sm font-medium">รหัสอ้างอิง</p>
                <p className="text-sm text-muted-foreground">
                  {user.refCode || 'ไม่ระบุ'}
                </p>
              </div>

              {/* <div className="space-y-2">
                <p className="text-sm font-medium">สถานะการยืนยัน</p>
                <Badge variant={user.isVerified ? 'default' : 'secondary'}>
                  {user.isVerified !== undefined
                    ? user.isVerified
                      ? 'ยืนยันแล้ว'
                      : 'ยังไม่ยืนยัน'
                    : 'ไม่ระบุ'}
                </Badge>
              </div> */}
            </div>

            <Separator />

            <div className="text-xs text-muted-foreground">
              <p>
                อัปเดตล่าสุด:{' '}
                {user.updatedAt
                  ? new Date(user.updatedAt).toLocaleString('th-TH')
                  : 'ไม่ระบุ'}
              </p>
            </div>
          </CardContent>
        </Card>

        <div className="col-span-2 flex justify-end gap-3">
          {isEmailProvider && (
            <Button onClick={openChangePasswordDialog.onTrue}>
              เปลี่ยนรหัสผ่าน
            </Button>
          )}
          <Button onClick={() => router.push(paths.profileEdit)}>
            แก้ไขข้อมูล
          </Button>
        </div>
      </div>

      <ChangePasswordDialog
        open={openChangePasswordDialog.value}
        onOpenChange={openChangePasswordDialog.onFalse}
      />
    </LoadingSection>
  )
}
